import { Link, useSearchParams } from 'react-router';
import type { ReleaseNoteDetail, ReleaseNoteItemType, ReleaseNoteItemView } from '@product-portal/shared';
import { FilterChips } from '@/components/portal/filter-chips';
import { Pill } from '@/components/portal/pill';
import { useProducts, useReleaseNote, useReleaseNotes, useUpcomingVersions } from '@/hooks/use-requests';
import { formatDate, formatDayMonth, formatLongDate } from '@/lib/format';
import { countsLabel, VERSION_STATE_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

function ListHeader({ children }: { children: string }) {
  return <div className="border-b border-border px-4 py-3 text-xs font-bold uppercase tracking-[0.04em] text-muted-foreground">{children}</div>;
}

/** Items of a note (ADR-005 §5): bold title, text, « Répond à PP-xxx (équipe) »; the counter is computed from them. */
function ItemsSection({ type, items }: { type: ReleaseNoteItemType; items: ReleaseNoteItemView[] }) {
  if (items.length === 0) return null;
  const feature = type === 'feature';
  const count = countsLabel(feature ? { features: items.length, fixes: 0 } : { features: 0, fixes: items.length });
  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="m-0 flex items-center gap-2 text-[15px] font-bold">
        <Pill className={cn('px-2 py-0.5 text-[11px]', feature ? 'bg-type-evolution text-type-evolution-foreground' : 'bg-muted text-foreground-secondary')}>
          {feature ? 'Nouveautés' : 'Corrections'}
        </Pill>
        {count}
      </h3>
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-3 text-sm leading-[1.55] text-body-strong">
            <span aria-hidden="true" className={cn('mt-2 size-1.5 shrink-0 rounded-full', feature ? 'bg-status-delivered-foreground' : 'bg-muted-foreground')} />
            <span>
              <strong className="font-bold">{item.title}</strong>
              {item.body && <> {item.body}</>}
              {item.request && (
                <span className="text-xs text-muted-foreground">
                  {' '}
                  Répond à{' '}
                  <Link to={`/demandes/${item.request.id}`} className="no-underline">
                    {item.request.reference}
                  </Link>{' '}
                  ({item.request.teamName})
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function NoteArticle({ note }: { note: ReleaseNoteDetail }) {
  const deliveredOn = note.deliveredOn ?? note.publishedAt;
  return (
    <article id="note" className="flex min-w-0 flex-[999_1_560px] flex-col rounded-card border border-border bg-card">
      <div className="flex flex-col gap-3 border-b border-border px-7 pb-5 pt-6">
        <div className="flex flex-wrap items-center gap-2.5">
          <Pill className="bg-muted text-foreground-secondary">{note.product.name}</Pill>
          {note.versionState === 'in_production' && (
            <Pill className="bg-status-delivered text-status-delivered-foreground">{VERSION_STATE_LABELS.in_production}</Pill>
          )}
          <span className="text-[13px] text-muted-foreground">Livrée le {formatLongDate(deliveredOn)}</span>
        </div>
        <h2 className="m-0 text-2xl font-bold tracking-[-0.01em]">
          Version {note.versionNumber} — {note.title}
        </h2>
        {note.summary && <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-body-strong">{note.summary}</p>}
      </div>
      <div className="flex flex-col gap-6 px-7 py-5">
        <ItemsSection type="feature" items={note.items.filter((i) => i.type === 'feature')} />
        <ItemsSection type="fix" items={note.items.filter((i) => i.type === 'fix')} />
        {note.goodToKnow.trim() && (
          <section className="flex flex-col gap-2.5">
            <h3 className="m-0 flex items-center gap-2 text-[15px] font-bold">
              <Pill className="bg-impact-annoying px-2 py-0.5 text-[11px] text-impact-annoying-foreground">À savoir</Pill>
            </h3>
            <div className="whitespace-pre-line rounded-control border border-warning-border bg-warning px-3.5 py-3 text-sm leading-[1.55] text-body-strong">
              {note.goodToKnow}
            </div>
          </section>
        )}
        {note.closedRequests.length > 0 && (
          <section className="flex flex-col gap-2.5 border-t border-border pt-4">
            <h3 className="m-0 text-sm font-bold text-foreground-secondary">Demandes du portail clôturées par cette version</h3>
            <div className="flex flex-wrap gap-2">
              {note.closedRequests.map((r) => (
                <Link
                  key={r.id}
                  to={`/demandes/${r.id}`}
                  className="inline-flex items-center gap-1.5 rounded-control border border-border px-2.5 py-1.5 text-[13px] text-foreground no-underline hover:text-foreground"
                >
                  <span className="text-muted-foreground">{r.reference}</span> {r.title}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}

/**
 * 05 · Release notes, published notes only (the API never returns drafts).
 * Not reproduced (see CLAUDE.md, « Écarts »): the email subscription
 * checkbox (OPEN-QUESTION delivery-subscription: no table stores it),
 * Confluence and guide links, ticket counts, archives link.
 */
export function ReleaseNotesPage() {
  const [params, setParams] = useSearchParams();
  const productId = params.get('produit') ?? 'all';
  const products = useProducts();
  const notes = useReleaseNotes(productId === 'all' ? undefined : productId);
  const upcoming = useUpcomingVersions(productId === 'all' ? undefined : productId);
  const selectedId = params.get('note') ?? notes.data?.[0]?.id ?? null;
  const note = useReleaseNote(selectedId);

  const select = (next: { produit?: string; note?: string | null }) => {
    const search = new URLSearchParams(params);
    if (next.produit !== undefined) {
      if (next.produit === 'all') search.delete('produit');
      else search.set('produit', next.produit);
      search.delete('note');
    }
    if (next.note) search.set('note', next.note);
    setParams(search, { replace: true });
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        <h1 className="m-0 text-[26px] font-black tracking-[-0.01em]">Release notes</h1>
        <p className="m-0 text-sm text-muted-foreground">
          Ce qui a été livré, produit par produit. Les notes sont rédigées par l'équipe produit et publiées ici à chaque mise en production.
        </p>
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">
          <FilterChips
            ariaLabel="Produit"
            value={productId}
            onChange={(value) => select({ produit: value })}
            options={[{ value: 'all', label: 'Tous' }, ...(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))]}
          />

          <div className="flex flex-col rounded-card border border-border bg-card">
            <ListHeader>À venir</ListHeader>
            {(upcoming.data ?? []).length === 0 && <p className="m-0 px-4 py-3.5 text-[13px] text-muted-foreground">Aucune version planifiée.</p>}
            {(upcoming.data ?? []).map((v) => (
              <div key={v.id} className="flex flex-col gap-1 border-b border-l-[3px] border-b-muted border-l-border px-4 py-3.5 last:border-b-0">
                <span className="text-sm font-bold text-foreground-secondary">
                  {v.product.name} {v.number}
                </span>
                <span className="text-xs text-muted-foreground">
                  {v.plannedDate ? `Prévue le ${formatDayMonth(v.plannedDate)} · ` : ''}
                  {VERSION_STATE_LABELS[v.state].toLowerCase()}
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-col rounded-card border border-border bg-card">
            <ListHeader>Livrées</ListHeader>
            {notes.data && notes.data.length === 0 && <p className="m-0 px-4 py-3.5 text-[13px] text-muted-foreground">Aucune release note publiée.</p>}
            {(notes.data ?? []).map((n) => {
              const active = n.id === selectedId;
              return (
                <button
                  key={n.id}
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  onClick={() => select({ note: n.id })}
                  className={cn(
                    'flex cursor-pointer flex-col gap-1 border-0 border-b border-l-[3px] border-b-muted px-4 py-3.5 text-left font-[inherit] text-foreground last:border-b-0',
                    active ? 'border-l-primary bg-accent' : 'border-l-transparent bg-card',
                  )}
                >
                  <span className="text-sm font-bold">
                    {n.product.name} {n.versionNumber}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(n.deliveredOn ?? n.publishedAt)}
                    {countsLabel(n.counts) && ` · ${countsLabel(n.counts)}`}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {note.data ? (
          <NoteArticle note={note.data} />
        ) : (
          <div className="flex min-w-0 flex-[999_1_560px] rounded-card border border-border bg-card px-7 py-6 text-sm text-muted-foreground">
            {note.isError ? 'Release note introuvable.' : selectedId ? 'Chargement…' : 'Aucune release note à afficher.'}
          </div>
        )}
      </div>
    </>
  );
}
